import React, { useEffect, useState } from 'react';
import { ApiOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import './ConnectionStatusIcon.css';

interface ConnectionStatusIconProps {
  isConnected: boolean;
  onReconnect: () => void;
}

const ConnectionStatusIcon: React.FC<ConnectionStatusIconProps> = ({ 
  isConnected, 
  onReconnect 
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [prevConnected, setPrevConnected] = useState(isConnected);

  useEffect(() => {
    // Only animate when connection state changes
    if (prevConnected !== isConnected) {
      setIsAnimating(true);
      setPrevConnected(isConnected);
      
      // Add page-wide ripple effect
      const rippleOverlay = document.createElement('div');
      rippleOverlay.className = `page-ripple-overlay ${isConnected ? 'ripple-connected' : 'ripple-disconnected'}`;
      document.body.appendChild(rippleOverlay);
      
      // Stop animation after it completes
      const timer = setTimeout(() => {
        setIsAnimating(false);
        if (document.body.contains(rippleOverlay)) {
          document.body.removeChild(rippleOverlay);
        }
      }, 2500);
      
      return () => {
        clearTimeout(timer);
        if (document.body.contains(rippleOverlay)) {
          document.body.removeChild(rippleOverlay);
        }
      };
    }
  }, [isConnected, prevConnected]);

  const tooltipContent = (
    <div className="neo-tooltip-content">
      <div className="neo-tooltip-title">
        {isConnected ? 'Connected to Core Platform' : 'Disconnected'}
      </div>
      <div className="neo-tooltip-subtitle">
        {isConnected ? 'All systems operational' : 'Click to reconnect'}
      </div>
    </div>
  );

  return (
    <Tooltip 
      title={tooltipContent}
      overlayClassName="neo-connection-tooltip"
      placement="bottom"
    >
      <div 
        className={`neo-connection-status ${isConnected ? 'status-connected' : 'status-disconnected'} ${isAnimating ? 'animating' : ''}`}
        onClick={isConnected ? undefined : onReconnect}
        role="button"
        tabIndex={0}
        aria-label={isConnected ? 'Connected to Core Platform' : 'Disconnected - Click to reconnect'}
      >
        {/* Core circle with socket icon */}
        <div className="neo-core-circle">
          <ApiOutlined className="neo-socket-icon" />
        </div>

        {/* Status indicator dot */}
        <div className="status-dot"></div>
      </div>
    </Tooltip>
  );
};

export default ConnectionStatusIcon;
